import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, concatMap, map, switchMap, tap } from "rxjs/operators";

import { AccountResponse } from "../../interfaces/account-response.interface";
import { Logon } from "../../interfaces/logon.interface";
import { UserService } from "../../services/user.service";
import { AnalyticsService } from "../../../../../shared/services/analytics.service";

import * as UserActions from "../actions/user.actions";
import { SiteSection } from "../../../../../shared/enums/site-section.enum";
import { CloneLogonSearchResponse } from "../../interfaces/cloneable-logon-search.interface";
import { UserMe } from "@serasa/core/modules/recertification/interfaces/user-me.interface";
import { IMyAudit } from "@serasa/core/pages/actions-history/models/deleted-user.interface";

@Injectable()
export class UserEffects {
  loadUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadLogonsList),
      concatMap((action) => {
        const searchUser = localStorage.getItem("searchValue") || null;
        if (searchUser) {
          return this._userService.searchByLogon(searchUser).pipe(
            map((accountResponse: AccountResponse) => {
              if (action.isPagination) {
                return UserActions.doTablePagination({ accountResponse });
              }

              return UserActions.loadLogonsListSuccess({ accountResponse });
            }),
            catchError((error: HttpErrorResponse) =>
              of(UserActions.loadAccountResponseFailure({ error })),
            ),
          );
        }

        return this._userService.getAllLogons(action.offset, action.limit).pipe(
          map((accountResponse: AccountResponse) => {
            if (action.isPagination) {
              return UserActions.doTablePagination({ accountResponse });
            }

            return UserActions.loadLogonsListSuccess({ accountResponse });
          }),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.loadAccountResponseFailure({ error })),
          ),
        )}
      ),
    );
  });

  loadUserMeInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadUserMeInfo),
      concatMap((action) =>
        this._userService.getUserMeInfo().pipe(
          map((userMeInfo: UserMe) =>
            UserActions.loadUserMeInfoSuccess({ userMeInfo }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.loadUserMeInfoFailure({ error })),
          ),
        ),
      ),
    );
  });

  updateUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateSelectedLogon),
      concatMap((action) =>
        this._userService
          .updateUserAccount(action.logon, action.userId, action.payload)
          .pipe(
            concatMap((res: any) => {
              const searchUser = localStorage.getItem("searchValue");
              if (searchUser) {
                return of(
                  UserActions.loadUpdateSelectedLogonSuccess({
                    payload: action.payload,
                  }),
                  UserActions.filterByLogon({ logon: searchUser || "" }),
                ).pipe(
                  tap(() => {
                    this._analyticsService.doCustomEvent(
                      {
                        events: ["portalLogons_EdicaoSucesso"],
                        flowName:
                          "MP:LG:IN:PortalDeLogons:EditarUsuarioSucesso",
                      },
                      "PortalDeLogonsEditarUsuarioSucesso",
                    );
                  }),
                );
              }
              return of(
                UserActions.loadUpdateSelectedLogonSuccess({
                  payload: action.payload,
                }),
                UserActions.loadLogonsList({
                  offset:
                    JSON.parse(
                      localStorage.getItem("mat-paginator") ||
                        '{"pageIndex":0}',
                    ).pageIndex || 0,
                  isPagination: false,
                  limit: 20,
                }),
              ).pipe(
                tap(() => {
                  this._analyticsService.doCustomEvent(
                    {
                      events: ["portalLogons_EdicaoSucesso"],
                      flowName: "MP:LG:IN:PortalDeLogons:EditarUsuarioSucesso",
                    },
                    "PortalDeLogonsEditarUsuarioSucesso",
                  );
                }),
              );
            }),
            catchError((error: HttpErrorResponse) =>
              of(UserActions.loadUpdateAccountResponseFailure({ error })).pipe(
                tap((error) => {
                  this._analyticsService.sendCustomError(
                    error.error,
                    SiteSection.ERRO_EDITAR_USUARIO,
                  );
                }),
              ),
            ),
          ),
      ),
    );
  });

  filterByLogon$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.filterByLogon),
      concatMap((action) =>
        this._userService.searchByLogon(action.logon).pipe(
          map((accountResponse: AccountResponse) =>
            UserActions.loadSearchSuccess({ accountResponse }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.loadAccountResponseFailure({ error })),
          ),
        ),
      ),
    );
  });

  loadLogon$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadSelectedLogon),
      concatMap((action) =>
        this._userService.getUserAccount(action.selectedUserId).pipe(
          map((selectedLogon: Logon) =>
            UserActions.loadSelectedLogonSuccess({
              selectedLogon: {
                ...selectedLogon,
                ...(action.userDataTable as Logon),
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.loadSelectedLogonFailure({ error })).pipe(
              tap((error) => {
                this._analyticsService.sendCustomError(
                  error.error,
                  SiteSection.ERRO_DETALHES_USUARIO,
                );
              }),
            ),
          ),
        ),
      ),
    );
  });

  cloneableLogonSearchData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadCloneableLogonSearchData),
      concatMap((action) =>
        this._userService.getCloneableLogonSearchData(action.logon).pipe(
          concatMap((cloneableLogonData: CloneLogonSearchResponse) =>
            of(
              UserActions.loadCloneableLogonSearchDataSuccess({
                cloneableLogon: {
                  ...cloneableLogonData,
                },
              }),
            ).pipe(
              tap(() => {
                this._analyticsService.doPageLoad(
                  `${SiteSection.CLONAR_USUARIO}:Busca:Sucesso`,
                  {
                    events: ["portalLogons_ClonarUsuarioBuscarSucesso"],
                  },
                );
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.loadCloneableLogonSearchDataFailure({ error })).pipe(
              tap((error) => {
                this._analyticsService.sendCustomError(
                  error.error,
                  SiteSection.ERRO_AO_BUSCAR_USUARIO_CLONAVEL,
                );
              }),
            ),
          ),
        ),
      ),
    );
  });

  cloneLogon$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.addClonedUser),
      concatMap((action) =>
        this._userService.cloneLogon(action.payload).pipe(
          concatMap((cloneableLogonData: CloneLogonSearchResponse) =>
            of(
              UserActions.addClonedUserSuccess({
                response: { ...cloneableLogonData },
              }),
            ).pipe(
              tap(() => {
                this._analyticsService.doPageLoad(
                  `${SiteSection.CLONAR_USUARIO}:Criar:Sucesso`,
                  {
                    events: ["portalLogons_ClonarUsuarioCriarSucesso"],
                  },
                );
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.addClonedUserFailure({ error })).pipe(
              tap((error) => {
                this._analyticsService.sendCustomError(
                  error.error,
                  SiteSection.ERRO_AO_CLONAR_USUARIO,
                );
              }),
            ),
          ),
        ),
      ),
    );
  });

  closeSideNav$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.closeSideNav),
      switchMap((_) =>
        of(
          UserActions.loadToggleSidenav({ openSidenav: false })
        ),
      ),
    );
  });

  resetUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.resetUser),
      concatMap((action) =>
        this._userService.resetPassword(action.resetUser).pipe(
          concatMap((_) =>
            of(UserActions.resetUserSuccess({ resetStatus: "success" })).pipe(
              tap(() => {
                this._analyticsService.doCustomEvent(
                  {
                    events: ["portalLogons_ResetSenhaSucesso"],
                    flowName: "MP:LG:IN:PortalDeLogons:RedefinirSenhaSucesso",
                  },
                  "PortalDeLogonsRedefinirSenhaSucesso",
                );
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              UserActions.resetUserFailure({ error, resetStatus: "error" }),
            ).pipe(
              tap((error) => {
                this._analyticsService.sendCustomError(
                  error.error,
                  SiteSection.ERRO_REDEFINIR_SENHA,
                );
              }),
            ),
          ),
        ),
      ),
    );
  });

  loadMyAudit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadMyAudits),
      switchMap((action) =>
        this._userService.getMyAudits(action.params).pipe(
          map((myAudits: IMyAudit) => {
            return UserActions.loadMyAuditsSuccess({ myAudits });
          }),
          catchError((httpError: HttpErrorResponse) => {
            return of(UserActions.loadMyAuditsFailure({ error: null })).pipe(
              tap(() => {
                this._analyticsService.sendCustomError(
                  httpError,
                  SiteSection.ERRO_LISTAGEM_USUARIOS_EXCLUIDOS,
                );
              }),
            );
          }),
        ),
      ),
    );
  });

  constructor(
    private readonly actions$: Actions,
    private readonly _userService: UserService,
    private readonly _analyticsService: AnalyticsService,
  ) {}
}
